/**
 * Hook für Channel Packages
 * 
 * Status und Regeneration von Channel Packages.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  getProductChannelPackages,
  getChannelPackage,
  regenerateChannelPackage,
  regenerateAllChannelPackages,
  resolveChannelPackage,
  getChannelPackageDisplayStatus,
} from '@/lib/api/channel-packages'
import type {
  ChannelPackage,
  ChannelPackageStatusInfo,
} from '@/types/channel-packages'

/**
 * Hook to fetch channel packages for a product
 */
export function useProductChannelPackages(productId: string | undefined) {
  return useQuery({
    queryKey: ['channel-packages', productId],
    queryFn: () => getProductChannelPackages(productId!),
    enabled: !!productId,
    staleTime: 30_000,
    refetchInterval: (query) => {
      // Refetch every 5 seconds if any package is processing
      const data = query.state.data
      if (data?.channel_packages?.some(p => 
        p.regenerate_status === 'processing' || 
        p.regenerate_status === 'pending'
      )) {
        return 5000
      }
      return false
    },
  })
}

/**
 * Hook to fetch a single channel package
 */
export function useChannelPackage(
  channelId: string | undefined,
  packageId: string | undefined
) {
  return useQuery({
    queryKey: ['channel-package', channelId, packageId],
    queryFn: () => getChannelPackage(channelId!, packageId!),
    enabled: !!channelId && !!packageId,
    staleTime: 30_000,
  })
}

/**
 * Hook to resolve channel package content
 */
export function useResolveChannelPackage(
  channelId: string | undefined,
  packageId: string | undefined
) {
  return useQuery({
    queryKey: ['channel-package-resolve', channelId, packageId],
    queryFn: () => resolveChannelPackage(channelId!, packageId!),
    enabled: !!channelId && !!packageId,
    staleTime: 60_000, // 1 minute
  })
}

/**
 * Hook for regenerating a channel package
 */
export function useRegenerateChannelPackage(
  channelId: string,
  packageId: string
) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (reason?: string) => regenerateChannelPackage(channelId, packageId, reason),
    onMutate: async () => {
      // Optimistic update: set status to processing
      await queryClient.cancelQueries({ 
        queryKey: ['channel-packages'] 
      })
      
      const previousPackages = queryClient.getQueryData(['channel-packages'])
      
      // Could add optimistic update here if needed
      
      return { previousPackages }
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.regenerationStarted'))
      } else {
        toast.error(data.error || t('toast.error.regeneration'))
      }
      // Always invalidate to get fresh data
      queryClient.invalidateQueries({ queryKey: ['channel-packages'] })
      queryClient.invalidateQueries({ queryKey: ['channel-package', channelId, packageId] })
    },
    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (context?.previousPackages) {
        queryClient.setQueryData(['channel-packages'], context.previousPackages)
      }
      toast.error(`Fehler: ${error.message}`)
    },
  })
}

/**
 * Hook for regenerating all channel packages for a product
 */
export function useRegenerateAllChannelPackages(productId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (reason?: string) => regenerateAllChannelPackages(productId, reason),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.bulkRegenerationStarted', { count: data.queued_count }))
      } else {
        toast.error(data.error || t('toast.error.regeneration'))
      }
      queryClient.invalidateQueries({ queryKey: ['channel-packages', productId] })
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`)
    },
  })
}

/**
 * Get status info for a channel package
 */
export function useChannelPackageStatus(
  pkg: ChannelPackage,
  contentUpdatedAt?: string
): ChannelPackageStatusInfo {
  return getChannelPackageDisplayStatus(
    {
      regenerate_status: pkg.regenerate_status,
      last_regenerated_at: pkg.last_regenerated_at,
    },
    contentUpdatedAt
  )
}

/**
 * Get channel icon based on channel type
 */
export function getChannelIcon(channelType: string | number | undefined): string {
  const typeStr = String(channelType ?? '').toLowerCase()
  
  // Support both string names and numeric IDs
  switch (typeStr) {
    case 'webshop':
    case '1':
      return 'bi-shop'
    case 'amazon':
    case '9':
      return 'bi-box-seam'
    case 'ebay':
    case '2':
      return 'bi-bag'
    case 'instagram':
      return 'bi-instagram'
    case 'facebook':
      return 'bi-facebook'
    case 'google':
      return 'bi-google'
    default:
      return 'bi-broadcast'
  }
}

/**
 * Format last regenerated time
 */
export function formatLastRegenerated(date: string | null): string {
  if (!date) return 'Nie'
  
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Gerade eben'
  if (diffMins < 60) return `vor ${diffMins} Min.`
  if (diffHours < 24) return `vor ${diffHours} Std.`
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`
  
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

